import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';

@Component({
  selector: 'app-work-order-card',
  templateUrl: './app-work-order-card.component.html',
  styleUrls: ['./app-work-order-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppWorkOrderCardComponent {
  /** The work order data object */
  @Input() order: any;

  /** Overrides if passed individually */
  @Input() orderName?: string;
  @Input() startDate?: string | Date;
  @Input() deliveryDate?: string | Date;
  @Input() budget?: number | string;
  @Input() total?: number | string;
  @Input() due?: number | string;
  @Input() progress?: number | string;
  @Input() customerName?: string;
  @Input() paymentStatus?: string;
  @Input() orderStatus?: string;

  /** Permissions & Action visibility */
  @Input() accessPermission: string = 'SALES_CONTACTS';
  @Input() showCustomerColumn: boolean = true;
  @Input() hasCustomCustomerColumn: boolean = false;
  @Input() showActionMenu: boolean = true;
  @Input() showEditAction: boolean = true;
  @Input() showCloneAction: boolean = false;
  @Input() showDeleteAction: boolean = true;
  @Input() showOrderStatusMenu: boolean = true;

  /** Styling & behavior */
  @Input() customClass: string = '';
  @Input() clickable: boolean = true;

  /** Event Emitters */
  @Output() cardClick = new EventEmitter<any>();
  @Output() titleClick = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<{ status: string; order: any }>();
  @Output() edit = new EventEmitter<any>();
  @Output() clone = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();

  get resolvedStartDate(): string | Date {
    return this.startDate !== undefined
      ? this.startDate
      : (this.order?.order_start_date || this.order?.start_date || '');
  }

  get resolvedDeliveryDate(): string | Date {
    return this.deliveryDate !== undefined
      ? this.deliveryDate
      : (this.order?.order_delivery_date || this.order?.delivery_date || '');
  }

  get rawOrderName(): string {
    return this.orderName !== undefined
      ? this.orderName
      : (this.order?.order_name || '-');
  }

  get orderNameDisplay(): string {
    const name = this.rawOrderName;
    if (name && name.length > 25) {
      return name.substring(0, 25) + '...';
    }
    return name || '-';
  }

  get resolvedBudget(): any {
    return this.budget !== undefined ? this.budget : this.order?.order_budget;
  }

  get resolvedTotal(): any {
    return this.total !== undefined ? this.total : this.order?.total;
  }

  get resolvedDue(): any {
    return this.due !== undefined ? this.due : this.order?.due;
  }

  get resolvedProgress(): any {
    return this.progress !== undefined ? this.progress : this.order?.order_progress;
  }

  get rawCustomerName(): string {
    return this.customerName !== undefined
      ? this.customerName
      : (this.order?.order_name_by_customer || this.order?.customer_name || '-');
  }

  get customerNameDisplay(): string {
    const name = this.rawCustomerName;
    if (name && name.length > 20) {
      return name.substring(0, 20) + '...';
    }
    return name || '-';
  }

  get rawPaymentStatus(): string {
    return (this.paymentStatus !== undefined ? this.paymentStatus : (this.order?.payment_status || '')).toLowerCase();
  }

  get paymentStatusDisplay(): string {
    const ps = this.rawPaymentStatus;
    if (ps === 'no payment' || ps === 'no payments' || ps === 'due') {
      return 'Due';
    } else if (ps === 'partially paid') {
      return 'Partially Paid';
    } else if (ps === 'no due' || ps === 'fully paid' || ps === 'paid') {
      return 'Paid';
    }
    return this.paymentStatus || this.order?.payment_status || '-';
  }

  get paymentStatusClass(): { [key: string]: boolean } {
    const ps = this.rawPaymentStatus;
    return {
      'due-badge': ps === 'no payment' || ps === 'no payments' || ps === 'due',
      'on-hold': ps === 'partially paid',
      'delivered': ps === 'no due' || ps === 'fully paid' || ps === 'paid',
    };
  }

  get resolvedOrderStatus(): string {
    return this.orderStatus !== undefined ? this.orderStatus : (this.order?.status || 'Yet to start');
  }

  get orderStatusBadgeClass(): { [key: string]: boolean } {
    const st = this.resolvedOrderStatus;
    return {
      'yet-start': st === 'Yet to start',
      'on-hold': st === 'On hold',
      'yet-progress': st === 'In progress',
      'delivered': st === 'Delivered',
    };
  }

  onCardClick(event: MouseEvent): void {
    if (this.clickable) {
      this.cardClick.emit(this.order);
    }
  }

  onTitleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.titleClick.emit(this.order);
  }

  onSelectStatus(status: string): void {
    this.statusChange.emit({ status, order: this.order });
  }

  onEdit(event: MouseEvent): void {
    event.stopPropagation();
    this.edit.emit(this.order);
  }

  onClone(event: MouseEvent): void {
    event.stopPropagation();
    this.clone.emit(this.order);
  }

  onDelete(event: MouseEvent): void {
    event.stopPropagation();
    this.delete.emit(this.order);
  }
}
